
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import MainLayout from './components/MainLayout';
import { isBookingDomain } from './config/portalMode';
import AdminMasterTemplatePage from './pages/admin/AdminMasterTemplatePage';
import AdminChecklistPage from './pages/admin/AdminChecklistPage';
import AdminLinksPage from './pages/admin/AdminLinksPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminSignoffPage from './pages/admin/AdminSignoffPage';
import AdminDataImportsPage from './pages/admin/AdminDataImportsPage';
import AdminDataExportsPage from './pages/admin/AdminDataExportsPage';
import AdminGroupsPage from './pages/admin/AdminGroupsPage';
import AdminDatabaseConfigPage from './pages/admin/AdminDatabaseConfigPage';
import AdminTableBrowserPage from './pages/admin/AdminTableBrowserPage';
import AdminFieldIntegrationDemo from './pages/admin/AdminFieldIntegrationDemo';
import AdminVoiceBoxSyncPage from './pages/admin/AdminVoiceBoxSyncPage';
import AdminMessageSuitePage from './pages/admin/AdminMessageSuitePage';
import AdminMessageTemplatesPage from './pages/admin/AdminMessageTemplatesPage';
import AdminDealershipOnboardingPage from './pages/admin/AdminDealershipOnboardingPage';
import AdminBrandTemplatesPage from './pages/admin/AdminBrandTemplatesPage';
import AdminImportedContactPlansPage from './pages/admin/AdminImportedContactPlansPage';
import AdminWorkflowBuilderPage from './pages/admin/AdminWorkflowBuilderPage';
import AdminWorkflowsPage from './pages/admin/AdminWorkflowsPage';
import AdminWorkflowTemplatesPage from './pages/admin/AdminWorkflowTemplatesPage';
import AdminBrandsPage from './pages/admin/AdminBrandsPage';
import AdminOEMsPage from './pages/admin/AdminOEMsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import ClientPortalUsersPage from './pages/admin/ClientPortalUsersPage';
import AdminCleanupPage from './pages/admin/AdminCleanupPage';
import JobListDashboard from './pages/joblist/JobListDashboard';
import Jobs from './pages/joblist/Jobs';
import JobDetail from './pages/joblist/JobDetail';
import Invoices from './pages/joblist/Invoices';
import InvoiceDetail from './pages/joblist/InvoiceDetail';
import Clients from './pages/joblist/Clients';
import Pipeline from './pages/joblist/Pipeline';
import Departments from './pages/joblist/Departments';
import FinancialYears from './pages/joblist/FinancialYears';
import AuditLogs from './pages/joblist/AuditLogs';
import XeroConfig from './pages/joblist/XeroConfig';
import Roles from './pages/joblist/Roles';
import RateCard from './pages/joblist/RateCard';
import SetupDealershipSelectorPage from './pages/setup/SetupDealershipSelectorPage';
import SetupDealershipDashboardPage from './pages/setup/SetupDealershipDashboardPage';
import DealershipChecklistPage from './pages/setup/DealershipChecklistPage';
import LocationConfigurationPage from './pages/setup/LocationConfigurationPage';
import DealershipSignoffPage from './pages/setup/DealershipSignoffPage';
import SetupOEMsPage from './pages/setup/SetupOEMsPage';
import EventsPage from './pages/events/EventsPage';
import AppointmentsPage from './pages/events/AppointmentsPage';
import CalendarIntegrationsPage from './pages/events/CalendarIntegrationsPage';
import EventConfigPage from './pages/events/EventConfigPage';
import PublicEventPage from './pages/events/PublicEventPage';
import EventPickerPage from './pages/events/EventPickerPage';
import EventReportsPage from './pages/events/EventReportsPage';
import EventReportsListPage from './pages/events/EventReportsListPage';
import ClientSecurityPage from './pages/events/ClientSecurityPage';
import DomainManagementPage from './pages/events/DomainManagementPage';
import HelpPage from './pages/help/HelpPage';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';

const AppRouter = ({ 
    role, 
    user,
    dealerships, 
    templates, 
    dropdownOptions, 
    handleUpdateDealership, 
    handleTemplateUpdate, 
    handleAddDealership, 
    handleDropdownUpdate, 
    handlePushTemplate,
    onLogout
}) => {

    const SetupPageWrapper = ({ page: PageComponent }) => {
        const { dealershipId } = useParams();
        const dealership = dealerships.find(d => d.id === dealershipId);
        if (!dealership) return <Navigate to="/setup" />;
        return <PageComponent dealership={dealership} masterTemplate={templates.checklist} onUpdateDealership={handleUpdateDealership} />;
    };

    const LocationPageWrapper = () => {
        const { dealershipId } = useParams();
        const dealership = dealerships.find(d => d.id === dealershipId);
        if (!dealership) return <Navigate to="/setup" />;
        return <LocationConfigurationPage dealership={dealership} factfindTemplate={templates.factfind} linksTemplate={templates.links} onUpdateDealership={handleUpdateDealership} />;
    };

    const SignoffPageWrapper = () => {
        const { dealershipId } = useParams();
        const dealership = dealerships.find(d => d.id === dealershipId);
        if (!dealership) return <Navigate to="/setup" />;
        return <DealershipSignoffPage dealership={dealership} masterTemplate={templates.signoff} onUpdateDealership={handleUpdateDealership} dropdownOptions={dropdownOptions} />;
    };

    const DealershipDashboardWrapper = () => {
        const { dealershipId } = useParams();
        const dealership = dealerships.find(d => d.id === dealershipId);
        if (!dealership) return <Navigate to="/setup" />;
        return <SetupDealershipDashboardPage dealership={dealership} />;
    }

    // Determine access flags — check both legacy `role` field and `roles` array
    const isAdmin = role === 'admin' || (Array.isArray(user?.roles) && user.roles.includes('admin'));
    const hasJobListAccess = user?.hasJobListAccess || isAdmin;
    const hasSetupAccess = user?.hasSetupAccess || isAdmin;

    const userPermissions = user?.permissions || [];
    const canViewWorkflows = userPermissions.includes('workflows.view') || userPermissions.includes('workflows.edit') || userPermissions.includes('workflows.edit_vb');
    const canEditWorkflows = userPermissions.includes('workflows.edit');
    const canEditVbWorkflows = userPermissions.includes('workflows.edit_vb');

    const isCustomHostname = () => {
        const hostname = window.location.hostname;
        const defaultDomain = process.env.REACT_APP_DEFAULT_DOMAIN || 'localhost';
        const knownDefaultDomains = [
            'clientbox.marketing.delivery',
            'localhost',
            '127.0.0.1',
            '0.0.0.0'
        ];
        const isDefaultDomain = knownDefaultDomains.includes(hostname) ||
            hostname === defaultDomain ||
            (defaultDomain !== 'localhost' && hostname.endsWith('.' + defaultDomain));
        return !isDefaultDomain &&
            !hostname.includes('127.0.0.1') &&
            !hostname.includes('0.0.0.0') &&
            !isBookingDomain() &&
            hostname.includes('.');
    };

    const CustomHostnameRoot = () => {
        const [mode, setMode] = useState(null);
        const [loading, setLoading] = useState(true);

        useEffect(() => {
            if (!isCustomHostname()) {
                setLoading(false);
                return;
            }
            const apiBase = isCustomHostname() ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:3000/api');
            fetch(`${apiBase}/events/by-domain`)
                .then((res) => res.ok ? res.json() : { count: 0, events: [] })
                .then((data) => {
                    const count = data.count || (data.events || []).length;
                    if (count === 1 && data.events && data.events[0]) {
                        const key = data.events[0].key || data.events[0].short_code || data.events[0].slug;
                        setMode(key ? { redirect: `/${key}` } : { picker: true });
                    } else {
                        setMode({ picker: true });
                    }
                })
                .catch(() => setMode({ picker: true }))
                .finally(() => setLoading(false));
        }, []);

        if (isCustomHostname()) {
            if (loading) {
                return (
                    <div className="min-h-screen flex items-center justify-center bg-gray-50">
                        <div className="text-center">
                            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                            <p className="text-gray-600">Loading...</p>
                        </div>
                    </div>
                );
            }
            if (mode?.redirect) {
                return <Navigate to={mode.redirect} replace />;
            }
            if (mode?.picker) {
                return <EventPickerPage />;
            }
        }

        if (!user || !role) {
            return <Navigate to="/login" replace />;
        }
        if (role === 'client') {
            return <Navigate to="/events/reports" replace />;
        }
        return (role === 'admin' || role === 'setup') ? <Navigate to="/setup" replace /> :
            (hasJobListAccess) ? <Navigate to="/joblist/dashboard" replace /> :
            <Navigate to="/login" replace />;
    };

    const CustomHostnameEventWrapper = () => {
        const { eventKey } = useParams();
        const segments = (useLocation().pathname || '').split('/').filter(Boolean);

        if (!isCustomHostname()) {
            return <Navigate to="/" replace />;
        }
        const reserved = ['login', 'auth', 'admin', 'joblist', 'setup', 'help', 'static', 'uploads', 'api', 'favicon.ico'];
        if (reserved.includes(eventKey) || segments.length !== 1) {
            return <Navigate to="/" replace />;
        }
        return <PublicEventPage identifierOverride={eventKey} />;
    };

    return (
        <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/login" element={<Login />} />
            
            {/* Root path - check for custom hostname first */}
            <Route path="/" element={<CustomHostnameRoot />} />

            {/* Custom hostname event path: /{key} - only when custom hostname to avoid matching /login, /setup, etc. */}
            {isCustomHostname() && (
                <Route path="/:eventKey" element={<CustomHostnameEventWrapper />} />
            )}
            
            {/* Public event booking page (no auth required) - must be before MainLayout routes */}
            <Route path="/events/public/:identifier" element={<PublicEventPage />} />

            {/* All authenticated routes wrapped in MainLayout */}
            {(user && role) && (
                <Route path="/*" element={
                    <MainLayout 
                        user={user}
                        role={role}
                        hasJobListAccess={hasJobListAccess}
                        hasSetupAccess={hasSetupAccess}
                        isAdmin={isAdmin}
                        canViewWorkflows={canViewWorkflows}
                        dealerships={dealerships}
                        onLogout={onLogout}
                    />
                }>
                    {/* Admin Routes */}
                    {isAdmin && (
                        <>
                            <Route path="admin/checklist" element={<AdminChecklistPage />} />
                            <Route path="admin/factfind" element={<AdminMasterTemplatePage title="Fact Find" template={templates.factfind} onTemplateUpdate={(t) => handleTemplateUpdate('factfind', t)} itemType="Field" templateKeyPrefix="ff" />} />
                            <Route path="admin/links" element={<AdminLinksPage template={templates.links} onTemplateUpdate={(t) => handleTemplateUpdate('links', t)} />} />
                            <Route path="admin/signoff" element={<AdminSignoffPage templates={templates} onTemplateUpdate={handleTemplateUpdate} dropdownOptions={dropdownOptions} dealerships={dealerships} onPushTemplate={(ids) => handlePushTemplate('signoff', ids)} />} />
                            <Route path="admin/groups" element={<AdminGroupsPage />} />
                            <Route path="admin/brands" element={<AdminBrandsPage />} />
                            <Route path="admin/oems" element={<AdminOEMsPage />} />
                            <Route path="admin/departments" element={<Departments />} />
                            <Route path="admin/users" element={<AdminUsersPage />} />
                            <Route path="admin/client-portal-users" element={<ClientPortalUsersPage />} />
                            <Route path="admin/roles" element={<Roles />} />
                            <Route path="admin/data-imports" element={<AdminDataImportsPage />} />
                            <Route path="admin/data-exports" element={<AdminDataExportsPage />} />
                            <Route path="admin/database-config" element={<AdminDatabaseConfigPage />} />
                            <Route path="admin/table-browser" element={<AdminTableBrowserPage />} />
                            <Route path="admin/field-integration" element={<AdminFieldIntegrationDemo />} />
                            <Route path="admin/voicebox-sync" element={<AdminVoiceBoxSyncPage />} />
                            <Route path="admin/settings" element={<AdminSettingsPage options={dropdownOptions} onUpdate={handleDropdownUpdate} />} />
                            <Route path="admin/cleanup" element={<AdminCleanupPage />} />
                            <Route path="admin/audit-logs" element={<AuditLogs />} />
                            <Route path="admin/financial-years" element={<FinancialYears />} />
                            <Route path="admin/xero-config" element={<XeroConfig />} />
                            <Route path="admin" element={<Navigate to="admin/checklist" replace />} />
                        </>
                    )}

                    {/* Setup Routes - accessible to both admin and setup users */}
                    {(hasSetupAccess || isAdmin) && (
                        <>
                            {canViewWorkflows && (
                                <>
                                    <Route path="admin/workflows" element={<AdminWorkflowsPage canEdit={canEditWorkflows} canEditVb={canEditVbWorkflows} />} />
                                    <Route path="admin/workflow-templates" element={<AdminWorkflowTemplatesPage />} />
                                    <Route path="admin/workflow-builder" element={<AdminWorkflowBuilderPage canEdit={canEditWorkflows} canEditVb={canEditVbWorkflows} />} />
                                    <Route path="admin/workflow-builder/:id" element={<AdminWorkflowBuilderPage canEdit={canEditWorkflows} canEditVb={canEditVbWorkflows} />} />
                                </>
                            )}
                            <Route path="admin/message-suites" element={<AdminMessageSuitePage />} />
                            <Route path="admin/message-templates" element={<AdminMessageTemplatesPage />} />
                            <Route path="admin/brand-templates" element={<AdminBrandTemplatesPage />} />
                            <Route path="admin/dealership-onboarding" element={<AdminDealershipOnboardingPage />} />
                            <Route path="admin/imported-contact-plans" element={<AdminImportedContactPlansPage />} />
                        </>
                    )}

                    {/* Job List & Invoicing Routes */}
                    {hasJobListAccess && (
                        <>
                            <Route path="joblist/dashboard" element={<JobListDashboard />} />
                            <Route path="joblist/jobs" element={<Jobs />} />
                            <Route path="joblist/jobs/create" element={<JobDetail />} />
                            <Route path="joblist/jobs/:id" element={<JobDetail />} />
                            <Route path="joblist/invoices" element={<Invoices />} />
                            <Route path="joblist/invoices/create" element={<InvoiceDetail />} />
                            <Route path="joblist/invoices/:id" element={<InvoiceDetail />} />
                            <Route path="joblist/clients" element={<Clients />} />
                            <Route path="joblist/pipeline" element={<Pipeline />} />
                            <Route path="joblist/rate-card" element={<RateCard />} />
                            <Route path="joblist" element={<Navigate to="joblist/dashboard" replace />} />
                        </>
                    )}

                    {/* Setup/Onboarding Routes */}
                    {hasSetupAccess && (
                        <>
                            <Route path="setup/oems" element={<SetupOEMsPage />} />
                            <Route path="setup" element={<SetupDealershipSelectorPage dealerships={dealerships} />} />
                            <Route path="setup/:dealershipId" element={<DealershipDashboardWrapper />} />
                            <Route path="setup/:dealershipId/checklist" element={<SetupPageWrapper page={DealershipChecklistPage} />} />
                            <Route path="setup/:dealershipId/locations" element={<LocationPageWrapper />} />
                            <Route path="setup/:dealershipId/signoff" element={<SignoffPageWrapper />} />
                        </>
                    )}

                    {/* Event Management Routes - internal (setup/admin) and client portal users */}
                    {(hasSetupAccess || isAdmin || role === 'client') && (
                        <>
                            <Route path="events" element={<EventsPage />} />
                            <Route path="events/reports" element={<EventReportsListPage />} />
                            <Route path="events/:eventId/reports" element={<EventReportsPage />} />
                            <Route path="events/appointments" element={<AppointmentsPage />} />
                            <Route path="events/calendar-integrations" element={<CalendarIntegrationsPage />} />
                            <Route path="events/security" element={<ClientSecurityPage />} />
                            <Route path="events/domains" element={<DomainManagementPage />} />
                            <Route path="events/:id" element={<EventConfigPage />} />
                        </>
                    )}

                    {/* Help & Documentation - accessible to all authenticated users */}
                    <Route path="help" element={<HelpPage />} />

                    {/* Default redirect */}
                    <Route 
                        path="" 
                        element={
                            role === 'client' ? <Navigate to="/events/reports" replace /> :
                            (role === 'admin' || role === 'setup') ? <Navigate to="/setup" replace /> :
                            (hasJobListAccess) ? <Navigate to="/joblist/dashboard" replace /> :
                            <Navigate to="/login" replace />
                        } 
                    />
                </Route>
            )}

            {/* Note: Root path "/" is handled above by CustomHostnameRoot */}
            {/* Fallback for unauthenticated users accessing non-public routes */}
            {(!user || !role) && (
                <>
                    {/* Catch-all for any other paths (not "/" which is handled above) */}
                    <Route 
                        path="*" 
                        element={
                            <Navigate to="/login" replace />
                        } 
                    />
                </>
            )}
        </Routes>
    );
};

export default AppRouter;
